import {DOM} from "/@lib@/commons/xml/dom.js";
import {findElementWedlet, findScrollCtn, findWedEditor, IFindInAreaWedlet, IParentWedlet, IVirtualisableWedlet, IWedlet, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {AgEltBoxSelection, IWedEditorBoxSel} from "/@back@/edit/wed/features/boxSel.js";
import {IWedletSingleElt} from "/@back@/edit/wed/wedlets/wedletSingleElt.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {ACTION} from "/@lib@/commons/actions.js";
import {LANG} from "/@lib@/commons/lang.js";
import {BoxContainer} from "/@back@/edit/wed/wedlets/box/boxTags.js";
import {REG} from "/@lib@/commons/registry.js";
import {WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {IJmlNode, IJmlSubSet} from "/@lib@/commons/xml/jml.js";

/**
 *
 * Attributs :
 * - skin : Attention : remplace le skin par défaut "box-pane".
 * - skinOver
 */
class BoxPane extends BoxContainer {

	protected head: HTMLElement;
	protected body: HTMLElement;

	protected _focusLstn: (wedMgr: WedMgr, focused: Element) => void;
	protected _pointerTarget: HTMLElement;
	protected _pointerLstn: (ev: PointerEvent) => void;
	protected _ctnResizer: ResizeObserver;

	get collapsed(): boolean {return !this.hasAttribute('opened')}

	set collapsed(state: boolean) {
		if (DOM.setAttrBool(this, 'opened', !state)) {
			if (state) this._doClose();
			else this._doOpen();
		}
	}

	configWedletElt(tpl: Element, wedlet: IWedlet) {
		this.wedlet = wedlet as IParentWedlet & IWedletSingleElt;
		const sh = this.shadowRoot || this.attachShadow(DOMSH.SHADOWDOM_INIT);
		WEDLET.installSkins(tpl, sh, wedlet, "box-pane");
		this.subEltWedlets = WEDLET.cloneWedletContent(this, tpl, wedlet as IWedletSingleElt, true);
		const head = this.head = this.shadowRoot.querySelector<HTMLElement>(".head");
		head.onclick = this.onClickHead;
		this.onkeydown = this.onKeyDown;
		this.body = this.shadowRoot.querySelector<HTMLElement>(".body");
		this.body.hidden = true;
		this.body.style.zIndex = "901";
		//Pour drag
		Object.defineProperty(wedlet as IFindInAreaWedlet, 'wedletRect', {
			get: ()=> {return this.collapsed ? undefined : this.body.getBoundingClientRect()}
		});
	}

	disconnectedCallback() {
		if (!this.collapsed) this._doClose();
	}

	protected _doClose() {
		this.body.hidden = true;
		const wedMgr = this.wedlet.wedMgr;
		wedMgr.listeners.removeListener("getFocus", this._focusLstn);
		if (this._pointerTarget) {
			this._pointerTarget.addEventListener("pointerdown", this._pointerLstn);
			this._pointerTarget = null
		}
		if (this._ctnResizer) {
			this._ctnResizer.disconnect();
			this._ctnResizer = null;
		}
		if (wedMgr.openedPaneWedlet === this.wedlet) wedMgr.openedPaneWedlet = null;
	}

	protected _doOpen() {
		this.style.position = "relative"; //permet de maintenir le pane ouvert stable pendant le scroll.
		this.body.hidden = false;
		WEDLET.refreshEditMode(this.wedlet);
		const ctn = this.findCtn();
		this.fixPosition(ctn, this.head, this.body);
		if (!this._focusLstn) this._focusLstn = (wedMgr: WedMgr, focused: Element) => {
			if (focused && !DOMSH.isAncestor(this, focused)) this.collapsed = true;
		};
		if (!this._pointerLstn) this._pointerLstn = (ev: PointerEvent) => {
			if (!DOMSH.isAncestor(this, ev.composedPath()[0] as Element)) this.collapsed = true;
		}
		const wedMgr = this.wedlet.wedMgr;
		wedMgr.listeners.on("getFocus", this._focusLstn);
		this._pointerTarget = findWedEditor(this).rootNode;
		this._pointerTarget?.addEventListener("pointerdown", this._pointerLstn);
		this._ctnResizer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
			this.fixPosition(entries[0].target as HTMLElement, this.head, this.body);
		});
		this._ctnResizer.observe(ctn);
		wedMgr.openedPaneWedlet = this.wedlet;
	}

	protected onClickHead(this: HTMLElement, ev: MouseEvent) {
		const me = DOMSH.findHost<BoxPane>(this);
		me.collapsed = !me.collapsed;
		ev.stopImmediatePropagation();
		ev.preventDefault();
	}

	protected onKeyDown(ev: KeyboardEvent) {
		if (ev.target === this && LANG.in(ev.key, 'ArrowRight', 'Enter', ' ') && !ACTION.isAccelPressed(ev)) {
			if (ev.key === 'ArrowRight') {
				this.collapsed = false;
				return; //On laisse l'event continuer pour que boxSel sel le prochain.
			}
			this.collapsed = !this.collapsed;
			ev.stopImmediatePropagation();
			ev.preventDefault();
		}
	}

	protected findCtn(): HTMLElement {
		return findScrollCtn(this);
	}

	protected paneLeft(ctn: HTMLElement): number {
		return ctn.getBoundingClientRect().left + 5;
	}

	protected paneWidth(ctn: HTMLElement): string {
		return (ctn.clientWidth - 11) + 'px';
	}

	protected fixPosition(ctn: HTMLElement, around: HTMLElement, target: HTMLElement) {
		const st = target.style;
		const aroundRect = around.getBoundingClientRect();
		const offset = (target.offsetParent || document.body).getBoundingClientRect();
		st.top = Math.round(aroundRect.bottom - offset.top) + 'px';
		st.left = Math.round(this.paneLeft(ctn) - offset.left) + 'px';
		st.width = st.maxWidth = this.paneWidth(ctn);
	}
}

AgEltBoxSelection(BoxPane, {selMode: 'box', actionsLists: ['actions:wed:box-ctn', 'actions:wed:box']});

REG.reg.registerSkin('box-pane', 1, /* language=CSS */ `
	:host {
		margin: .3em;
	}

	:host([opened]) {
		background-color: var(--edit-select-bgcolor);
	}

	:host(:focus) {
		outline: var(--focus-outline);
	}

	.head {
		cursor: pointer;
		border: 1px solid var(--border-color);
		border-radius: 4px;
		padding: .3em;
		background-color: var(--edit-btn-bgcolor);
		color: var(--edit-btn-color);
		font-weight: bold;
	}

	:host(.virtual) .head {
		font-weight: normal;
	}

	[hidden] {
		display: none !important;
	}

	.body {
		display: flex;
		flex-direction: column;
		box-sizing: border-box;
		background-color: var(--edit-bgcolor);
		position: absolute;
		border: solid 1px var(--border-color);
		border-radius: 4px;
		box-shadow: 1px 1px 5px var(--fade-color);
		padding: .2em .3em;
	}
`);
window.customElements.define("box-pane", BoxPane);

/**
 * Case à cocher forçant la création du wedlet courant.
 * Utilisé pour les box-pane en mode onChildrenEmptied="preserve"
 * Attributs :
 * - title-virtual : titre pour créer le contenu
 * - title-exist : titre pour supprimer le contenu
 * - del-confirm : [optionnel] texte de confirmation de la suppression.
 * - evt-propag : [optionnel] si existe event.stopPropagation() n'est pas appelé au click (utile si exploité dans le head d'un box-pane)
 */
class BoxExist extends BoxContainer {

	configWedletElt(tpl: Element, wedlet: IWedlet) {
		this.wedlet = wedlet as IParentWedlet & IWedletSingleElt;
		const sh = this.shadowRoot || this.attachShadow(DOMSH.SHADOWDOM_INIT);
		WEDLET.installSkins(tpl, sh, wedlet, "box-exist");
		super.configWedletElt(tpl, wedlet)
		this.addEventListener("click", this);
		this.addEventListener("keydown", this);
		this.tabIndex = 0;
	}

	refreshBindValue(val: IJmlNode, children?: IJmlSubSet) {
		super.refreshBindValue(val, children);
		this.title = this.getAttribute(this.wedlet.isVirtual() ? "title-virtual" : "title-exist") || "";
	}

	async handleEvent(this: BoxExist, ev: Event) {
		if (ev.type === 'click' || (ev instanceof KeyboardEvent && (ev.keyCode == 13 || ev.keyCode == 32))) {
			if (!WEDLET.isWritableWedlet(this.wedlet)) return;
			const eltWedlet = findElementWedlet(this);
			if (this.wedlet.isVirtual()) {
				WEDLET.insertDatasFromDisplay(eltWedlet.wedlet as IVirtualisableWedlet, null);
			} else {
				const confirm = this.getAttribute("del-confirm");
				if (!confirm || await POPUP.confirm(confirm, this, {okLbl: "Supprimer"})) {
					(this.wedlet.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr?.deleteContentSelection();
				}
			}
			ev.preventDefault();
			if (!this.hasAttribute("evt-propag")) ev.stopPropagation();
		}
	}
}

REG.reg.registerSkin('box-exist', 1, /* language=CSS */ `
	:host {
		display: inline-block;
		margin: 0 .2em;
		width: .5em;
		height: .5em;
		padding: .1em;
		vertical-align: baseline;
		background-color: var(--edit-btn-color);
		background-clip: content-box;
		border: 1px solid var(--border-color);
	}

	:host(.virtual) {
		background-color: transparent;
	}

	:host(:focus) {
		outline: var(--focus-outline);
	}
`);

window.customElements.define("box-exist", BoxExist);